import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X, MessageSquareWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function buildNoShowSms(booking) {
  const first = (booking.name || "there").trim().split(" ")[0] || "there";
  const vehicle = booking.vehicle || "vehicle";
  return `Hi ${first}, your AutoHire STL reservation for the ${vehicle} has been canceled because you did not arrive for pickup or contact us. This has been recorded as a no-call/no-show. If you believe this is an error, please reply to this message.`;
}

export default function NoShowConfirm({ booking, token, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const smsPreview = buildNoShowSms(booking);
  const hasPhone = !!booking.phone;
  const hasEmail = !!booking.email;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/no-show`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      const dnrNote = data.dnr_added
        ? `${data.dnr_added} added to the Do-Not-Rent list.`
        : "No email or phone on file, so nothing could be added to the Do-Not-Rent list.";
      if (data.already_processed) {
        toast.info("This booking was already marked no-call/no-show — no changes made.");
      } else if (data.sms_sent) {
        toast.success(`Canceled as no-call/no-show. SMS sent. ${dnrNote}`);
      } else if (!hasPhone) {
        toast.warning(`Canceled as no-call/no-show. SMS skipped — no phone on file. ${dnrNote}`);
      } else {
        toast.warning(`Canceled as no-call/no-show, but the SMS FAILED to send${data.sms_error ? ` (${data.sms_error})` : ""}. ${dnrNote}`);
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark this booking no-call/no-show.");
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="no-show-confirm-modal">
      <div className="w-full max-w-lg bg-[#0C0C0C] border border-red-500/40 rounded-2xl p-6 relative">
        <button data-testid="no-show-confirm-close" onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors">
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 text-red-400 text-sm font-black uppercase tracking-widest">
          <AlertTriangle size={16} /> Canceled — No-call/no-show
        </div>
        <h2 className="font-display font-black text-xl text-white mt-2">Confirm this action</h2>

        <div className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-white/45">Customer</span><span className="text-white/90">{booking.name}</span></div>
          <div className="flex justify-between"><span className="text-white/45">Vehicle</span><span className="text-white/90">{booking.vehicle}</span></div>
          <div className="flex justify-between"><span className="text-white/45">Pickup date</span><span className="text-white/90">{booking.pickup_date}</span></div>
          <div className="flex justify-between"><span className="text-white/45">Phone</span><span className="text-white/90">{booking.phone || "— none on file —"}</span></div>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-white/50 text-xs font-semibold uppercase tracking-wide mb-1.5">
            <MessageSquareWarning size={13} /> Exact SMS that will be sent
          </div>
          <p data-testid="no-show-sms-preview" className="text-sm text-white/85 bg-black/40 border border-white/10 rounded-lg p-3 leading-relaxed">
            {smsPreview}
          </p>
        </div>

        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/[0.06] p-3.5 text-xs text-red-100/90 space-y-1.5">
          <p className="font-bold text-red-300">This will, immediately and permanently:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Cancel this booking (agreements voided, vehicle freed for rebooking)</li>
            {(hasEmail || hasPhone) ? (
              <li><b>Add {booking.name} to the Do-Not-Rent list</b> — this blocks ALL future reservation attempts from this {hasEmail && hasPhone ? "email/phone" : hasEmail ? "email" : "phone"}, not just this booking. There is no separate "no-show only" list; this is the same list used for permanent rental bans.</li>
            ) : (
              <li><b>Cannot add to the Do-Not-Rent list</b> — this booking has no email or phone on file, so there is nothing to block.</li>
            )}
            {hasPhone ? (
              <li>Send the SMS above (one time, no email)</li>
            ) : (
              <li className="text-amber-300">SMS will be SKIPPED — no phone number is on file for this booking</li>
            )}
            <li>Stop any pending payment/return reminders for this booking</li>
          </ul>
          <p>No fees, charges, or forfeitures are added. This is separate from an ordinary cancellation and cannot be undone from this dialog.</p>
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          <button data-testid="no-show-confirm-cancel" onClick={onClose} disabled={busy}
            className="text-sm text-white/60 hover:text-white px-4 py-2 transition-colors disabled:opacity-50">
            Go back
          </button>
          <button data-testid="no-show-confirm-submit" onClick={confirm} disabled={busy}
            className="flex items-center gap-2 text-sm font-bold bg-red-600 text-white px-5 py-2.5 rounded-md hover:bg-red-500 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <AlertTriangle size={16} />}
            Confirm — Cancel & Mark No-Show
          </button>
        </div>
      </div>
    </div>
  );
}
